import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FN210 } from '../../services/n211Api';
import { useN211Mode } from './hooks/useN211Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useN211Record } from './hooks/useN211Record';
import { useN211Navigation } from './hooks/useN211Navigation';
import { useN211Permissions } from './hooks/useN211Permissions';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import ActionBar from './components/ActionBar';
import { getStatusBadge } from './helpers';
import { n211Api } from '../../services/n211Api';
import { BatchAcceptanceBridge, pwn211f1AcceptanceManifest } from '../../features/acceptance';
import './N211Form.css';

const N211Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useN211Permissions();
  const { mode, setMode, isEditing } = useN211Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = useN211Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useN211Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        message.error('載入清單失敗');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, (result: FN210) => {
        setMode('browse');
        clearFieldErrors();
        applyResult(result);
        // Refresh list to include new record
        loadList().then(ids => {
          const idx = ids.indexOf(result.fn210_01);
          if (idx >= 0) setCurrentIdx(idx);
        });
      });
    } else {
      // Enter add mode
      setMode('add');
      setActiveTab(0);
      clearNames();
      clearFieldErrors();
      resetForAdd();
    }
  }, [isEditing, mode, handleSave, setMode, clearNames, clearFieldErrors, resetForAdd, applyResult, loadList, setCurrentIdx]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      setMode('browse');
      revertToRecord();
      if (record) resolveAllNames(record);
    } else {
      // Enter edit mode
      if (record?.fn210_80 === 'P') {
        message.warning('已核准的記錄不可修改');
        return;
      }
      setMode('edit');
      clearFieldErrors();
    }
  }, [isEditing, record, setMode, revertToRecord, resolveAllNames, clearFieldErrors]);

  const handleDeleteAction = useCallback(() => {
    if (!record) return;
    if (record.fn210_80 === 'P') {
      message.warning('已核准的記錄不可刪除');
      return;
    }
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除買收單 ${record.fn210_01} 嗎？`,
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecordWithNames(ids[newIdx]);
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames]);

  const handleExit = useCallback(() => {
    navigate('/erp');
  }, [navigate]);

  const statusBadge = getStatusBadge(record);

  return (
    <PermissionGuard permission={`${Modules.N211}_QUERY`}>
      <div className="n211-form">
        <div className="page-wrapper">
          {/* Header */}
          <div className="form-header">
            <h2>
              客訴工廠買收交運單
              {statusBadge && (
                <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
              )}
            </h2>
            {loading && <span className="loading-indicator">載入中...</span>}
          </div>

          {/* Tabs */}
          <div className="tab-bar">
            <button
              className={`tab-btn ${activeTab === 0 ? 'active' : ''}`}
              onClick={() => setActiveTab(0)}
            >
              基本資料
            </button>
            <button
              className={`tab-btn ${activeTab === 1 ? 'active' : ''}`}
              onClick={() => setActiveTab(1)}
            >
              交運明細
            </button>
          </div>

          {/* Tab content */}
          <div className="tab-content">
            {activeTab === 0 && (
              <Tab1BasicInfo
                formData={formData}
                names={names}
                isEditing={isEditing}
                mode={mode}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
                getFieldError={getFieldError}
              />
            )}
            {activeTab === 1 && (
              <Tab2Details
                details={details}
                selectedRow={selectedRow}
                isEditing={isEditing}
                onSelectRow={setSelectedRow}
                onSetDetailField={setDetailField}
                onAddRow={addDetailRow}
                onDeleteRow={deleteDetailRow}
              />
            )}
          </div>
        </div>

        {/* ActionBar */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onNavTo={(idx) => navTo(idx, isEditing)}
          onExit={handleExit}
        />
        <BatchAcceptanceBridge
          manifest={pwn211f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fn210_01 ? [record.fn210_01] : []}
          keyOf={(item) => item.fn210_01}
          queryExact={(key) => n211Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => navTo(0, isEditing)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>
    </PermissionGuard>
  );
};

export default N211Form;
